import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import useSEO from "@/hooks/useSEO";
import SmartImage from "@/components/SmartImage";
import { ArrowUpRight } from "lucide-react";

const FOUNDER_PHOTO = "/founder/archana-founder-large.jpg";
const ATELIER_PHOTO = "/atelier/atelier-1.jpg";

const STATS = [
  { value: "2022", label: "Founded" },
  { value: "70+", label: "Hospitality & retail projects" },
  { value: "Pan-India", label: "Rollout capability" },
  { value: "End-to-end", label: "In-house production" },
];

export default function About() {
  useSEO({
    title: "About Auraa · Founder-Led Hospitality Uniform Design in India",
    description:
      "Auraa Designs is a founder-led hospitality uniform atelier in Bhubaneswar, founded in 2022 by fashion designer Archana Panda.",
    path: "/about",
  });

  return (
    <div data-testid="about-page">
      <Hero />
      <FounderSection />
      <AtelierSection />
      <CapabilityStrip />
      <BottomCTA />
    </div>
  );
}

function Hero() {
  const ref = useReveal();
  return (
    <section className="pt-28 md:pt-36 lg:pt-44 pb-16 md:pb-24">
      <div className="container-luxury">
        <div ref={ref} className="reveal max-w-4xl">
          <div className="flex items-center gap-4 mb-7">
            <span className="h-px w-10 bg-oxblood" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-oxblood font-medium">
              About Auraa
            </span>
          </div>
          <h1 className="font-serif text-[44px] sm:text-[56px] md:text-[72px] lg:text-[84px] leading-[1.02] tracking-[-0.02em] text-espresso text-balance">
            Hospitality identity,
            <br />
            <em className="italic text-oxblood">designed in cloth.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-[16px] md:text-[18px] leading-[1.75] text-espresso-soft font-light">
            Auraa designs and manufactures uniform programmes for hospitality
            brands across India. Founded in 2022 by fashion designer Archana
            Panda, with in-house production and a single standard of design
            integrity from concept to national rollout.
          </p>
        </div>
      </div>
    </section>
  );
}

function FounderSection() {
  const imgRef = useReveal();
  const copyRef = useReveal();
  return (
    <section
      data-testid="about-founder"
      className="py-24 md:py-36 bg-bone-200/40 border-y border-borderwarm"
    >
      <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div ref={imgRef} className="reveal lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative aspect-[4/5] overflow-hidden bg-sandstone">
            <PhotoOrFallback
              src={FOUNDER_PHOTO}
              alt="Archana Panda — Founder, Auraa Designs"
              monogram="A"
            />
          </div>
          <div className="mt-5 text-[10px] uppercase tracking-[0.32em] text-espresso-soft">
            Archana Panda · Founder & Designer
          </div>
        </div>

        <div ref={copyRef} className="reveal reveal-delay-2 lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium mb-5">
            01 · The Founder
          </div>
          <h2 className="font-serif text-[34px] md:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.015em] text-espresso text-balance">
            A fashion-design discipline,
            <br />
            <em className="italic text-oxblood">applied to hospitality.</em>
          </h2>

          <div className="mt-9 space-y-5 text-[15px] md:text-[16px] leading-[1.85] text-espresso-soft font-light">
            <p>
              Archana Panda founded Auraa Designs in 2022 to bring fashion-design
              discipline into hospitality uniforms — a category historically
              treated as procurement, not design.
            </p>
            <p>
              A trained fashion designer, Archana spent her early career at some
              of India's largest fashion houses, working across product, design
              and brand-build functions. Her work has been showcased at Lakmé
              Fashion Week and on national design platforms.
            </p>
            <p>
              Auraa is the application of that fashion sensibility to a different
              problem — staff uniforms as a brand surface for hotels,
              restaurants and premium hospitality. Every Auraa programme is led
              directly by Archana from first conversation to final fit.
            </p>
            <p className="text-espresso">
              The atelier and production facility are based in Bhubaneswar, with
              rollout capability across India.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AtelierSection() {
  const imgRef = useReveal();
  const copyRef = useReveal();
  return (
    <section data-testid="about-atelier" className="py-24 md:py-36">
      <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div ref={copyRef} className="reveal lg:col-span-7 lg:order-2">
          <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium mb-5">
            02 · The Atelier
          </div>
          <h2 className="font-serif text-[34px] md:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.015em] text-espresso text-balance">
            One roof. One standard.
            <br />
            <em className="italic text-oxblood">From sample to rollout.</em>
          </h2>

          <div className="mt-9 space-y-5 text-[15px] md:text-[16px] leading-[1.85] text-espresso-soft font-light">
            <p>
              Auraa operates from an integrated design atelier and production
              facility — pattern-making, sampling, fabric handling, and final
              manufacturing under one roof. This is deliberate. The hospitality
              uniform brief is too detailed, too revision-heavy, and too
              commercially serious to fragment across vendors.
            </p>
            <p>
              Every project moves through eight defined stages — discovery,
              brand immersion, moodboards, sampling, measurements, production,
              quality, rollout — held to a single standard.
            </p>
            <p className="text-espresso">
              Our clients receive one point of design ownership, one point of
              operational accountability, and one delivered standard across
              single boutique properties or multi-city portfolios.
            </p>
          </div>
        </div>

        <div ref={imgRef} className="reveal reveal-delay-2 lg:col-span-5 lg:order-1">
          <div className="relative aspect-[4/5] overflow-hidden bg-sandstone">
            <PhotoOrFallback
              src={ATELIER_PHOTO}
              alt="Auraa atelier — Bhubaneswar"
              monogram="◊"
            />
          </div>
          <div className="mt-5 text-[10px] uppercase tracking-[0.32em] text-espresso-soft">
            The Auraa Atelier · Bhubaneswar
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityStrip() {
  return (
    <section data-testid="about-capability-strip" className="py-20 md:py-28 bg-espresso text-bone">
      <div className="container-luxury">
        <div className="text-[10px] uppercase tracking-[0.32em] text-champagne mb-10 md:mb-14">
          Capability · At a Glance
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
          {STATS.map((s, i) => (
            <Stat key={i} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label, tbd }) {
  return (
    <div className="bg-espresso p-7 md:p-10 lg:p-12">
      <div className="font-serif text-[36px] md:text-[44px] lg:text-[52px] leading-none tracking-tight text-bone">
        {value}
      </div>
      <div className="mt-4 text-[12px] uppercase tracking-[0.24em] text-bone/70 leading-relaxed">
        {label}
      </div>
      {tbd && (
        <div className="mt-3 inline-block text-[9px] uppercase tracking-[0.28em] text-champagne border border-champagne/40 px-2 py-0.5">
          TBD — verify
        </div>
      )}
    </div>
  );
}

function BottomCTA() {
  const ref = useReveal();
  return (
    <section className="py-28 md:py-40">
      <div className="container-narrow text-center" ref={ref}>
        <div className="reveal">
          <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium mb-6">
            Next
          </div>
          <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[60px] leading-[1.05] tracking-[-0.015em] text-espresso text-balance">
            Start a hospitality uniform
            <br />
            <em className="italic text-oxblood">consultation.</em>
          </h2>
          <a
            href="https://calendly.com/archanaura8/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-bottom-cta"
            className="btn-primary group mt-10"
          >
            Book Consultation
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </div>
    </section>
  );
}

function PhotoOrFallback({ src, alt, monogram }) {
  return (
    <SmartImage
      src={src}
      alt={alt}
      monogram={monogram}
      className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1600ms] ease-out"
    />
  );
}
